import React, {
  useCallback,
  useContext,
  useRef,
  useState,
  useMemo,
  useEffect,
} from 'react';
import format from 'string-template';
import * as Sentry from '@sentry/browser';
import { forEachSeries } from 'p-iteration';
import { useRouteMatch } from 'react-router-dom';

import CopyAllIcon from '@mui/icons-material/CopyAll';
import SaveIcon from '@mui/icons-material/Save';
import Switch from '@mui/material/Switch';

import { pathPlaceholder, storagePaths } from '../../../../../utils/firebasePaths';
import useStorage, { StorageProcessState } from '../../../../../hooks/useStorage';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import useToast from '../../../../hooks/useToast';
import { BIRTHDAY_MESSAGE_PLACEHOLDER } from '../../../../utils/appCustomization';
import AttachmentsContainer from '../../../../components/AttachmentsContainer';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import { MAX_FILE_UPLOAD_SIZE, MAX_FILE_UPLOAD_SIZE_MB } from '../../../../../utils/files';
import useAttachmentInputHandlers from '../../../../../hooks/useAttachmentInputHandlers';

import commonTexts from '../texts.json';
import { Title, Description } from '../../styles';
import {
  PlaceholderButton,
  PlaceholdersContainer,
  PlaceholderTip,
} from '../../../../components/PlaceHolder/styles';
import {
  ContentWrapper,
  InputContainer,
  StyledTextField,
  StyledButton,
  BirthdayMessageContainer,
  StyledFormControlLabel,
} from './styles';
import screenTexts from './texts.json';

const MAX_CHARACTERS_COUNT = 5000;

const texts = {
  ...commonTexts,
  ...screenTexts,
};

const BirthdayMessageConfig = () => {
  const { coachDoc, isReady } = useContext(ExternalCoachContext);
  const { params: { userId: coachId } } = useRouteMatch();

  const { uploadAttachments, deleteAttachments, getBlobUrl } = useStorage();
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { onFilesSelected, removeFileByIndex, copyPlaceholderValue } = useAttachmentInputHandlers({
    showToast,
    fileSizeErrorText: texts.fileSizeError,
    maxFileSizeBytes: MAX_FILE_UPLOAD_SIZE,
    maxFileSizeMb: MAX_FILE_UPLOAD_SIZE_MB,
    placeholderCopiedText: texts.placeholderCopied,
  });

  const [isEnabled, setIsEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const [feedNotificationEnabled, setFeedNotificationEnabled] = useState(false);
  const [fileAttachments, setFileAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [attachmentsLoaded, setAttachmentsLoaded] = useState(false);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!coachDoc || !isReady || initializedRef.current) return;
    initializedRef.current = true;

    const {
      enabled = false,
      message: savedMessage = '',
      feedNotificationEnabled: savedFeedNotif = false,
      attachments: savedAttachments = [],
    } = coachDoc.birthdayMsgConfig;

    setIsEnabled(enabled);
    setMessage(savedMessage);
    setCharacterCount(savedMessage.length);
    setFeedNotificationEnabled(savedFeedNotif);

    const loadAttachments = async () => {
      const files = [];
      await forEachSeries(savedAttachments, async (attachment) => {
        const dataUrl = await getBlobUrl(attachment.url);
        files.push({ ...attachment, dataUrl, isUploaded: true });
      });
      if (isComponentMountedRef.current) {
        setFileAttachments(files);
        setAttachmentsLoaded(true);
      }
    };

    loadAttachments();
  }, [coachDoc, isReady, getBlobUrl, isComponentMountedRef]);

  const uploadBirthdayAttachments = useCallback(async (attachments) => {
    const attachmentsRefs = attachments.reduce((arr, current) => {
      if (current.isUploaded) {
        arr.push({ name: current.name, url: current.url });
      }
      return arr;
    }, []);

    const filesToUpload = attachments.filter((file) => !file.isUploaded);
    const storagePath = format(storagePaths.COACH_BIRTHDAY_MESSAGE_ATTACHMENTS, {
      [pathPlaceholder.COACH_ID]: coachId,
    });

    setIsUploading(true);
    try {
      const result = await uploadAttachments(filesToUpload, storagePath);

      let storageTaskFailed = false;
      const successfulUploads = [];
      result.forEach((res, index) => {
        if (res.state === StorageProcessState.SUCCESS) {
          successfulUploads.push({ name: filesToUpload[index].name, url: res.fileRef });
        } else {
          storageTaskFailed = true;
        }
      });

      if (storageTaskFailed && successfulUploads.length) {
        const filesToDelete = successfulUploads.map(({ url }) => ({ storagePath: url }));
        await deleteAttachments(filesToDelete);
      }

      attachmentsRefs.push(...successfulUploads);
      if (isComponentMountedRef.current) {
        setIsUploading(false);
      }
      return { attachmentsRefs, storageTaskFailed };
    } catch (error) {
      if (isComponentMountedRef.current) {
        setIsUploading(false);
      }
      Sentry.captureException(error, { extra: { coachId } });
      return { storageTaskFailed: true };
    }
  }, [coachId, uploadAttachments, deleteAttachments, isComponentMountedRef]);

  const handleSave = useCallback(async () => {
    let attachments = [];
    if (isEnabled && fileAttachments.length) {
      const { storageTaskFailed, attachmentsRefs } = await uploadBirthdayAttachments(fileAttachments);
      if (storageTaskFailed) {
        showToast(texts.uploadError, { error: true });
        return;
      }
      attachments = attachmentsRefs;
    }

    await coachDoc.setBirthdayMsgConfig({
      enabled: isEnabled,
      message,
      attachments,
      feedNotificationEnabled,
    });

    showToast(texts.messageSaved);
  }, [
    coachDoc,
    isEnabled,
    message,
    feedNotificationEnabled,
    fileAttachments,
    uploadBirthdayAttachments,
    showToast,
  ]);

  const handleTextChange = useCallback((event) => {
    const newValue = event.target.value;
    setMessage(newValue);
    setCharacterCount(newValue.length);
  }, []);

  const isError = useMemo(() => characterCount > MAX_CHARACTERS_COUNT, [characterCount]);
  const isLoading = !isReady || !attachmentsLoaded;
  const isSaveDisabled = isLoading || isError || (isEnabled && !message.trim());

  return (
    <ContentWrapper>
      <Title>{texts.title}</Title>
      <Description>{texts.description}</Description>

      <StyledFormControlLabel
        control={(
          <Switch
            checked={isEnabled}
            onChange={(event) => setIsEnabled(event.target.checked)}
            color="primary"
            disabled={isLoading}
          />
        )}
        label={texts.enableLabel}
      />

      {isEnabled && (
        <BirthdayMessageContainer>
          <InputContainer>
            <StyledTextField
              value={message}
              onChange={handleTextChange}
              placeholder={texts.defaultMessage}
              error={isError}
              helperText={format(texts.characterCount, {
                count: characterCount,
                maxCount: MAX_CHARACTERS_COUNT,
              })}
              multiline
              maxRows={18}
            />
            <AttachmentsContainer
              fileAttachments={fileAttachments}
              onFileRemoved={(index) => removeFileByIndex(index, setFileAttachments)}
              onFilesSelected={(event) => onFilesSelected(event, setFileAttachments)}
              isUploading={isUploading}
            />
            <StyledFormControlLabel
              control={(
                <Switch
                  checked={feedNotificationEnabled}
                  onChange={(event) => setFeedNotificationEnabled(event.target.checked)}
                  color="primary"
                />
              )}
              label={texts.feedNotificationLabel}
            />
          </InputContainer>
          <PlaceholdersContainer>
            <PlaceholderTip>{texts.placeholderTitle}</PlaceholderTip>
            {Object.values(BIRTHDAY_MESSAGE_PLACEHOLDER).map((placeholderValue) => (
              <PlaceholderButton
                key={placeholderValue}
                startIcon={<CopyAllIcon />}
                onClick={() => copyPlaceholderValue(placeholderValue)}
              >
                {`{${placeholderValue}}`}
              </PlaceholderButton>
            ))}
            <PlaceholderTip>{texts.placeholderDescription}</PlaceholderTip>
          </PlaceholdersContainer>
        </BirthdayMessageContainer>
      )}

      <StyledButton
        variant="contained"
        onClick={handleSave}
        disabled={isSaveDisabled}
        startIcon={<SaveIcon />}
      >
        {texts.saveButton}
      </StyledButton>

      <LoadingOverlay isLoading={isLoading} />
    </ContentWrapper>
  );
};

export default BirthdayMessageConfig;
